import { Button } from '@monorepo/shared/componentsV2/Button';
import { Page } from '@monorepo/shared/componentsV2/fullPageWizard/styled/fullPageWizard';
import { useSkipWidgetLink } from '@monorepo/shared/componentsV2/SkipWidgetLink';
import { useOpenClose } from '@monorepo/shared/hooks/useOpenClose';
import PlayIcon from '@svg/play.svg';
import React, { useCallback, useMemo, useState } from 'react';
import styled from 'styled-components';
import { useFormTemplateContext } from '../EditFormTemplatePage/FormTemplateContext';
import { WizardStep } from '../shared/consts';
import { FormTemplateHeader } from '../shared/FormTemplateHeader';
import { AddFormTemplateField } from './AddFormTemplateField';
import { AddFormTemplateSection } from './AddFormTemplateSection';
import { EditFormTemplateFields } from './EditFormTemplateFields';
import { FormPreviewModal } from './FormPreviewModal/FormPreviewModal';
import { InspectionDateSelector } from './InspectionDateSelector';
import { ReorderableFormTemplateGroupsList } from './ReorderableFormTemplateGroupsList';
import { SectionHeader } from './SectionHeader';
import { ColumnWrapper, MainContent, Sidebar, StyledMain } from './styled';
import { EditableFormTemplate } from './types';
import { VersionWarning } from './VersionWarning';

const PreviewButton = styled(Button)`
  margin-right: 0.5rem;
`;

export type FormTemplateEditorProps = {
  isNavigatingInWizardEnabled?: boolean;
  formWizardBasePath: string;
  isEditMode: boolean;
  organizationId?: string;
  sidebarComponent?: React.ReactNode;
  sidebarWidth?: string;
  isEditingDisabled: boolean;
  isSubmitting: boolean;
  onSaveFormTemplate: (formTemplate: EditableFormTemplate) => void;
};

// todo@formTemplates LLENA: rename to FormTemplateFieldEditor
export function FormTemplateEditor({
  isNavigatingInWizardEnabled = true,
  formWizardBasePath,
  isEditMode,
  organizationId,
  sidebarComponent,
  sidebarWidth,
  isEditingDisabled,
  isSubmitting,
  onSaveFormTemplate,
}: FormTemplateEditorProps) {
  const {
    SkipLink: SkipLinkToFormFields,
    SkipLinkTarget: SkipLinkTargetToFormFields,
  } = useSkipWidgetLink();
  const {
    SkipLink: SkipLinkToAddingNewFields,
    SkipLinkTarget: SkipLinkTargetToAddingNewFields,
  } = useSkipWidgetLink();

  const {
    formTemplateHelpers: { activeGroupSlug, formTemplate },
    isDirty,
  } = useFormTemplateContext();

  // maintain preview project state across closing&reopening preview modal
  const [projectIdForPreview, setProjectIdForPreview] = useState<string>();

  const activeGroup = useMemo(() => {
    const activeGroupByActiveGroupSlug = formTemplate?.groups.find(
      (group) => group.slug === activeGroupSlug,
    );
    const firstGroup = formTemplate?.groups[0];
    return activeGroupByActiveGroupSlug || firstGroup;
  }, [activeGroupSlug, formTemplate]);

  const activeGroupFields = activeGroup?.fields || [];
  const isInDuplicableSection = activeGroup?.isDuplicable || false;

  const handleSubmit = useCallback(() => {
    onSaveFormTemplate(formTemplate);
  }, [onSaveFormTemplate, formTemplate]);

  const [isPreviewModalOpen, openPreviewModal, closePreviewModal] =
    useOpenClose();

  return (
    <Page>
      <FormTemplateHeader
        organizationId={organizationId}
        isNavigatingInWizardEnabled={isNavigatingInWizardEnabled}
        canSubmit={isDirty}
        formWizardBasePath={formWizardBasePath}
        isEditMode={isEditMode}
        isSubmitting={isSubmitting}
        onSubmit={handleSubmit}
        rightActions={
          organizationId && (
            <PreviewButton onClick={openPreviewModal} variant="outlined">
              <PlayIcon aria-hidden />
              Preview
            </PreviewButton>
          )
        }
        wizardStep={WizardStep.FIELDS}
      />
      <StyledMain>
        <ColumnWrapper>
          <Sidebar $sidebarWidth={sidebarWidth}>
            <SkipLinkToFormFields linkText="Skip to form fields" />
            {sidebarComponent}
            <InspectionDateSelector isEditingDisabled={isEditingDisabled} />
            <ReorderableFormTemplateGroupsList
              activeGroupSlug={activeGroupSlug}
              isEditingDisabled={isEditingDisabled}
            />
            <AddFormTemplateSection isAddingDisabled={isEditingDisabled} />
          </Sidebar>
          <MainContent>
            <SkipLinkTargetToFormFields />
            {activeGroupSlug && formTemplate && (
              <>
                {isEditMode && isDirty && <VersionWarning />}
                <SectionHeader
                  activeGroupSlug={activeGroupSlug}
                  isEditingDisabled={isEditingDisabled}
                />
                <SkipLinkToAddingNewFields linkText="Skip to adding new fields" />
                <EditFormTemplateFields
                  isInDuplicableSection={isInDuplicableSection}
                  orderedFields={activeGroupFields || []}
                  isEditingDisabled={isEditingDisabled}
                />
                <SkipLinkTargetToAddingNewFields />
                <AddFormTemplateField
                  isInDuplicableSection={isInDuplicableSection}
                  isAddingDisabled={isEditingDisabled}
                />
              </>
            )}
          </MainContent>
          {organizationId && isPreviewModalOpen && (
            <FormPreviewModal
              isOpen={isPreviewModalOpen}
              onClose={closePreviewModal}
              organizationId={organizationId}
              projectId={projectIdForPreview}
              setProjectId={setProjectIdForPreview}
            />
          )}
        </ColumnWrapper>
      </StyledMain>
    </Page>
  );
}
